import { BooleanInput, maxLength, RadioButtonGroupInput, TextInput, useNotify, useRecordContext } from "react-admin";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { Grid, Typography } from "@mui/material";

import { settings } from "app";
import { erasmusRequests } from "api/erasmusApi";
import FormModal from "server/components/Modals/FormModal";
import { documentErrorMessage } from "server/components/DownloadFile";
import { download } from "server/download";

// Every input is optional - the backend fills in the participant, the rector, the host university,
// the academic year, the dates and the funding breakdown itself, and prints an empty field as blank
// (an unchecked box as "[ ]"). The form sources are the payload keys with a lower-case first letter,
// so the payload is built from the source instead of repeating each key.
const toPayloadKey = source => source.charAt(0).toUpperCase() + source.slice(1);

const TEXT_SECTIONS = [
  {
    title: "Договор и самоличност",
    fields: [
      { source: "contractNumber", label: "Номер на договора" },
      { source: "identityDocumentType", label: "Вид документ за самоличност" },
      { source: "identityCardNumber", label: "Номер на документа" },
      { source: "citizenship", label: "Гражданство" },
    ],
  },
  {
    title: "Обучение",
    fields: [
      { source: "studyType", label: "Вид обучение" },
      { source: "fieldOfKnowledge", label: "Област на знание" },
      { source: "fieldOfKnowledgeCode", label: "Код на областта" },
      { source: "languageLevelCheckedKey", label: "Ниво на езика" },
    ],
  },
  {
    title: "Банкови данни",
    fields: [
      { source: "bankName", label: "Банка" },
      { source: "bic", label: "BIC" },
      { source: "accountNumber", label: "Номер на сметка" },
      { source: "bankBranchAddress", label: "Адрес на клона", md: 12 },
    ],
  },
  {
    title: "Подписанти",
    fields: [
      { source: "chiefAccountantName", label: "Главен счетоводител" },
      { source: "chiefLawyerName", label: "Юрисконсулт" },
    ],
  },
];

const TEXT_SOURCES = TEXT_SECTIONS.flatMap(section => section.fields.map(field => field.source));

// mutually exclusive - a radio group keeps at most one of the three true, and the extra "none"
// choice is what lets a coordinator undo a pick (a radio cannot be unselected)
const FUNDING_TYPE_SOURCE = "fundingType";
const FUNDING_TYPE_NONE = "none";
const FUNDING_TYPES = [
  { id: "fundingEuGrant", name: "Грант от ЕС" },
  { id: "fundingZeroGrant", name: "Нулев грант" },
  { id: "fundingMixed", name: "Смесено финансиране" },
];
const FUNDING_TYPE_CHOICES = [...FUNDING_TYPES, { id: FUNDING_TYPE_NONE, name: "Без" }];

// independent of each other - any combination is valid
const SUPPORT_FIELDS = [
  { source: "supportIndividualLong", label: "Индивидуална подкрепа - дългосрочна" },
  { source: "supportIndividualShort", label: "Индивидуална подкрепа - краткосрочна" },
  { source: "supportStudyExtra", label: "Добавка за обучение" },
  { source: "supportTransport", label: "Транспорт" },
  { source: "supportExtraTravelDays", label: "Допълнителни дни за пътуване" },
  { source: "supportExpensiveTransport", label: "Скъпоструващ транспорт" },
  { source: "supportInclusion", label: "Приобщаване" },
];

const SectionHeading = ({ title }) => (
  <Grid
    item
    xs={12}
  >
    <Typography
      color="primary"
      sx={{ fontWeight: "bold" }}
    >
      {title}
    </Typography>
  </Grid>
);

const FundingContractDialog = ({ label = "Договор за мобилност" }) => {
  const record = useRecordContext();
  const notify = useNotify();

  if (!record) return null;

  const handleConfirm = async data => {
    const payload = {
      // an untouched text field is left out entirely - the backend prints it as blank either way
      ...TEXT_SOURCES.reduce((acc, source) => {
        const value = data[source]?.trim();
        if (value) acc[toPayloadKey(source)] = value;
        return acc;
      }, {}),
      ...FUNDING_TYPES.reduce((acc, { id }) => ({ ...acc, [toPayloadKey(id)]: data[FUNDING_TYPE_SOURCE] === id }), {}),
      ...SUPPORT_FIELDS.reduce((acc, { source }) => ({ ...acc, [toPayloadKey(source)]: !!data[source] }), {}),
    };

    try {
      await download(
        `${settings.gatewayApi}/${erasmusRequests.postFundingContract(record.id)}`,
        `funding-contract-${record.id}.pdf`,
        "POST",
        payload,
      );
    } catch (err) {
      // keep the modal open so the entered data can be corrected and the download retried
      notify(documentErrorMessage(err), { type: "error" });
      return false;
    }
  };

  return (
    <FormModal
      titleContent={label}
      dialogSize="md"
      saveTitle="Генерирай"
      onConfirm={handleConfirm}
      startIcon={<HandshakeIcon />}
      defaultValues={{ [FUNDING_TYPE_SOURCE]: FUNDING_TYPE_NONE }}
      titleButton={label}
      btnStyle={{ width: "max-content" }}
      variant="contained"
    >
      <Grid
        container
        spacing={2}
      >
        {TEXT_SECTIONS.map(({ title, fields }) => (
          <Grid
            item
            xs={12}
            key={title}
          >
            <Grid
              container
              spacing={2}
            >
              <SectionHeading title={title} />
              {fields.map(({ source, label: fieldLabel, md = 6 }) => (
                <Grid
                  item
                  xs={12}
                  md={md}
                  key={source}
                >
                  <TextInput
                    source={source}
                    label={fieldLabel}
                    fullWidth
                    validate={[maxLength(200)]}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}

        <SectionHeading title="Тип финансиране" />
        <Grid
          item
          xs={12}
        >
          <RadioButtonGroupInput
            source={FUNDING_TYPE_SOURCE}
            label={false}
            choices={FUNDING_TYPE_CHOICES}
            helperText={false}
          />
        </Grid>

        <SectionHeading title="Видове подкрепа" />
        {SUPPORT_FIELDS.map(({ source, label: fieldLabel }) => (
          <Grid
            item
            xs={12}
            md={6}
            key={source}
          >
            <BooleanInput
              source={source}
              label={fieldLabel}
            />
          </Grid>
        ))}
      </Grid>
    </FormModal>
  );
};

export default FundingContractDialog;
